import React, { useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import RenderLink from '../../../components/RenderLink';
import * as RouteConstants from '../../../constants/routeConstants';
import styles from './MyCover.module.scss';
import trackEvent from '../../../services/AnalyticsService';
import CollapsibleSection from './CollapsibleSection';

const WhoIsCovered = ({ policy, customer, extraPersonUpgradeAllowed }) => {
  const navigate = useNavigate();

  const items = useMemo(() => {
    if (!policy?.policyInfo?.Members) return [];

    // Frontend workaround applied as part of MYR-17853 to show lead member from customer data instead of policy
    return policy?.policyInfo?.Members?.filter((m) => !m.LeadMember).map((item) => ({
      fullName: [
        item.Title,
        item.FirstName,
        item.Surname,
      ].join(' ').trim(),
    }));
  }, [policy]);

  const translation = {
    title: 'Who\'s covered',
    description: 'The people listed below are covered as a driver or passenger in any vehicle.',
    actions: {
      add: 'Add a person',
      edit: 'Edit',
    },
  };

  const canEdit = policy?.policyInfo?.CanEditAdditionalMembers === true;
  let ctaButtonLabel = translation.actions.add;
  let ctaDisabled = false;

  if (!extraPersonUpgradeAllowed) {
    ctaDisabled = true;
    ctaButtonLabel = undefined;
  }

  const onAdd = useCallback(() => {
    if (!extraPersonUpgradeAllowed) { return; }

    trackEvent('MyRAC_MyCover_WhosCoveredAdd');
    navigate({
      pathname: RouteConstants.PRODUCT_UPGRADE_PAGE,
      search: '?product=Extra Person',
    });
  }, [navigate, extraPersonUpgradeAllowed]);

  return (
    <CollapsibleSection title={translation.title}>
      <p className="my-3">{translation.description}</p>
      <div className="clearfix flex-column my-2">
        <div className="item d-flex justify-content-between py-2">
          {/* Lead Member */}
          <div>
            <b>{customer.custInfo?.Title} {customer.custInfo?.Forename} {customer.custInfo?.Surname} (You)</b>
          </div>
        </div>
        {/* Additional Members */}
        {items.map((item) => (
          <div key={item.fullName} className="item d-flex justify-content-between py-2">
            <div>
              <b>{item.fullName}</b>
            </div>
            {canEdit && (
              <RenderLink
                url={RouteConstants.PERSONAL_DETAILS}
                className={styles.actionLink}
                analytics="MyRAC_MyCover_WhosCoveredEdit"
              >
                {translation.actions.edit}
              </RenderLink>
            )}
          </div>
        ))}
      </div>
      {ctaButtonLabel && (
        <button
          className={`${styles.outlineButton} my-1 px-4`}
          onClick={onAdd}
          type="button"
          disabled={ctaDisabled}
        >
          {ctaButtonLabel}
        </button>
      )}
    </CollapsibleSection>
  );
};

const mapStateToProps = (state) => ({
  policy: state.policy,
  customer: state.customer,
});

export default connect(mapStateToProps)(WhoIsCovered);
